import React, { createContext, useCallback, useState } from 'react';
import _get from 'lodash/get';

import { PageErrorUi } from 'components';
import Loader from './Loader';
import { useFetch } from 'utils/hooks';

/**
 * @type {React.Context<{
 *  reasons: any[],
 *  onUpdateReasons: (data: any, mode: 'update' | 'delete') => void,
 *  onReload: () => void
 * }}
 */
const RequestReasonListContext = createContext();

export const useRequestReasonList = () => {
  const context = React.useContext(RequestReasonListContext);
  if (!context) {
    throw new Error(
      'useRequestReasonList must be used within a RequestReasonListProvider'
    );
  }
  return context;
};

export function RequestReasonListProvider({ children = null }) {
  const [reasons, setReasons] = useState([]);

  const { isLoading, isError, fetchData: onReload } = useFetch(
    'configure.reasons.root',
    {
      onTransform: data => _get(data, 'request_reasons', []),
      onSuccess: setReasons
    }
  );

  const onUpdateReasons = useCallback(
    /**
     * @param {object} data;
     * @param {'update' | 'delete'} mode
     */
    (data = null, mode = 'update') => {
      if (!data) return;
      const reasonId = _get(data, 'id', '');
      setReasons(prevReasons => {
        if (mode === 'delete') {
          return prevReasons.filter(reason => {
            const id = _get(reason, 'id', '');
            return id !== reasonId;
          });
        }
        return prevReasons.map(reason => {
          const id = _get(reason, 'id', '');
          return id === reasonId ? { ...reason, ...data } : reason;
        });
      });
    },
    []
  );

  return (
    <RequestReasonListContext.Provider
      value={{ reasons, onUpdateReasons, onReload }}
    >
      <PageErrorUi
        isError={isError}
        onReload={onReload}
        msg="Unable to load the request reasons at the moment!"
      >
        <Loader isLoading={isLoading}>{children}</Loader>
      </PageErrorUi>
    </RequestReasonListContext.Provider>
  );
}
